import { Controller } from "@hotwired/stimulus"

// Pantalla del ranking: cada producto es un formulario con guardado manual (a
// propósito: el autoguardado se probó y dio más errores). Este controlador
// solo señaliza alrededor de ese guardado:
//   - marca la fila en cuanto se toca un campo (clase .is-dirty),
//   - avisa al salir de la página con filas sin guardar,
//   - tras guardar una fila, lleva el foco a la siguiente fila pendiente.
//
// Uso: data-controller="dirty-rows" en el contenedor de las dos tablas, con
// data-dirty-rows-leave-message-value para el texto del aviso.
//
// También lleva el botón "guardar todas" de cada cabecera
// (data-action="click->dirty-rows#saveAll" data-dirty-rows-type-param="top|old").
export default class extends Controller {
  static values = { leaveMessage: String }

  connect() {
    this.onChange = (event) => this.markDirty(event)
    this.onBeforeUnload = (event) => {
      if (!this.hasDirtyRows()) return
      event.preventDefault()
      event.returnValue = ""
    }
    this.onBeforeVisit = (event) => {
      if (this.hasDirtyRows() && !window.confirm(this.leaveMessageValue)) event.preventDefault()
    }
    this.onStreamRender = (event) => {
      this.collapseBulkToasts(event)
      this.beforeRowReplaced(event)
    }

    this.element.addEventListener("change", this.onChange)
    this.element.addEventListener("input", this.onChange)
    window.addEventListener("beforeunload", this.onBeforeUnload)
    document.addEventListener("turbo:before-visit", this.onBeforeVisit)
    document.addEventListener("turbo:before-stream-render", this.onStreamRender)
  }

  disconnect() {
    this.element.removeEventListener("change", this.onChange)
    this.element.removeEventListener("input", this.onChange)
    window.removeEventListener("beforeunload", this.onBeforeUnload)
    document.removeEventListener("turbo:before-visit", this.onBeforeVisit)
    document.removeEventListener("turbo:before-stream-render", this.onStreamRender)
  }

  markDirty(event) {
    const field = event.target
    if (!field.matches("input, select, textarea")) return
    const row = this.rowFor(field)
    if (row) row.classList.add("is-dirty")
  }

  // Los campos llevan form="form-<id>" y la fila es tr#tr-ranking-price-<id>.
  rowFor(field) {
    const formId = field.getAttribute("form")
    if (formId) return this.element.querySelector(`#tr-ranking-price-${formId.replace("form-", "")}`)
    return field.closest("tr")
  }

  hasDirtyRows() {
    return this.element.querySelector("tr.is-dirty") !== null
  }

  // Guardar todas las filas de una tabla. Turbo solo mantiene UN envío de
  // formulario en vuelo (Navigator.submitForm cancela el anterior), así que
  // pulsar los N botones seguidos dejaba solo la última petición. Aquí se
  // encadenan: cada fila se envía cuando Turbo ha terminado con la anterior.
  async saveAll(event) {
    event.preventDefault()
    const trigger = event.currentTarget
    if (this.bulkSaving) return

    const type = event.params.type
    const buttons = [...this.element.querySelectorAll(`.form-button-ranking-price-${type}`)]
    if (buttons.length === 0) return

    this.bulkSaving = true
    trigger.classList.add("is-busy")
    trigger.setAttribute("aria-busy", "true")
    try {
      for (const button of buttons) {
        if (!button.isConnected || !button.form) continue
        await this.submitAndWait(button)
      }
    } finally {
      this.bulkSaving = false
      trigger.classList.remove("is-busy")
      trigger.removeAttribute("aria-busy")
    }
  }

  // Pulsa el botón de guardar de una fila y resuelve cuando Turbo emite
  // turbo:submit-end para ese formulario (o, por si acaso, a los 20 s).
  submitAndWait(button) {
    return new Promise((resolve) => {
      const form = button.form
      let timer = null
      const done = () => {
        clearTimeout(timer)
        document.removeEventListener("turbo:submit-end", onEnd)
        resolve()
      }
      const onEnd = (event) => {
        if (event.target === form) done()
      }
      document.addEventListener("turbo:submit-end", onEnd)
      timer = setTimeout(done, 20000)
      button.click()
    })
  }

  // Durante "guardar todas" cada respuesta trae su toast de éxito; se deja
  // solo el último para no apilar uno por fila. Los de error se conservan.
  collapseBulkToasts(event) {
    if (!this.bulkSaving) return
    const stream = event.target
    if (stream.getAttribute("action") !== "append" || stream.getAttribute("target") !== "toast-container") return
    document.querySelectorAll("#toast-container .toast.toast--success").forEach((toast) => toast.remove())
  }

  // Al guardar, el servidor reemplaza la fila por stream. Antes de que
  // desaparezca la antigua se localiza la siguiente pendiente (con color de
  // estado) y, una vez inicializados los TomSelect de la nueva, se enfoca.
  beforeRowReplaced(event) {
    const stream = event.target
    if (stream.getAttribute("action") !== "replace") return
    const target = stream.getAttribute("target") || ""
    if (!target.startsWith("tr-ranking-price-")) return

    const oldRow = document.getElementById(target)
    if (!oldRow || !this.element.contains(oldRow)) return
    // Durante "guardar todas" no se persigue la siguiente fila: la página
    // saltaría de fila en fila con cada respuesta.
    if (this.bulkSaving) return

    const next = this.nextPendingRow(oldRow)
    if (!next) return

    const nextId = next.id
    setTimeout(() => this.focusRow(document.getElementById(nextId)), 120)
  }

  // Siguiente fila de producto con color de estado, en orden de documento y
  // sin pararse al final de la tabla: en la cola de revisión hay una tabla
  // por categoría, y en la pantalla del ranking dos (top y caídos). Las
  // filas separadoras de ranking no tienen color y se saltan.
  nextPendingRow(row) {
    const rows = [...this.element.querySelectorAll("tr[id^='tr-ranking-price-']")]
    const from = rows.indexOf(row)
    if (from === -1) return null
    return rows.slice(from + 1).find((tr) => /\btable-(warning|secondary|info|primary|danger)\b/.test(tr.className)) || null
  }

  focusRow(row) {
    if (!row) return
    const select = row.querySelector("select.js-tomselect")
    if (select?.tomselect) {
      select.tomselect.focus()
    } else {
      row.querySelector("input:not([type=hidden]), select")?.focus()
    }
    row.scrollIntoView({ block: "center", behavior: "smooth" })
  }
}
